/* eslint-disable */
// Session 55 · Phase 3 — Alpha Command Center.
// Cam's daily home page: System Health + Marketplace + Action Required
// + Live Feed + Alpha Checklist widget.

import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const STATUS_COLORS = {
  green: "#83B26E", yellow: "#B8913A", red: "#C05353",
  ok: "#83B26E", not_configured: "#B8913A", unknown: "#999", down: "#C05353", error: "#C05353",
};

const AREA_ICONS = {
  booking: "🗓", stripe: "💳", search: "🔍", email: "✉", notifications: "🔔",
  community: "💬", mobile: "📱", testflight: "🛫", legal: "📜", admin: "🛡", analytics: "📈",
};

const fmtMoney = (cents) => `$${(cents / 100).toLocaleString(undefined, {maximumFractionDigits: 0})}`;

export default function AdminCommandCenter() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true); setErr("");
    try {
      const r = await api.get("/admin/command-center");
      setData(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setRefreshing(false); }
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user]);
  useEffect(() => {
    if (user?.role !== "admin") return;
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [user]);

  if (loading) return <div style={{padding:40}}>Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;
  if (!data) return <div style={{padding:40}} data-testid="cc-loading">Loading command center…</div>;

  const sys = data.system_health || {};
  const mkt = data.marketplace_health || {};
  const act = data.action_required || {};
  const feed = data.live_feed || [];
  const cl = data.alpha_checklist || {};

  return (
    <div style={{padding:"32px 40px", maxWidth:1400, margin:"0 auto", fontFamily:"Jost, sans-serif", color:"#1B2340"}} data-testid="admin-command-center">
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16}}>
        <div>
          <span className="m-eyebrow" style={{color:"var(--gold)"}}>Admin · Alpha Command Center</span>
          <h1 style={{fontFamily:"Cormorant Garamond, serif", fontSize:34, margin:"4px 0 0", fontWeight:500}}>Today at Métier</h1>
          <div style={{fontSize:11, color:"rgba(27,35,64,0.5)", marginTop:4}}>
            Refreshed {new Date(data.generated_at).toLocaleTimeString()} · auto-refresh every 60s
          </div>
        </div>
        <div style={{display:"flex", gap:8}}>
          <LaunchPartnerButton />
          <FixZipsButton />
          <FixStatesButton />
          <FixNeighborhoodsButton />
          <BackfillCardPhotosButton />
          <ResyncWorkspaceLocationsButton />
          <GeocodeMissingButton />
          <button type="button" onClick={load} disabled={refreshing}
            data-testid="cc-refresh"
            style={{padding:"7px 14px", background:"#1B2340", color:"#FAF8F3", border:"none", borderRadius:999, fontSize:12, cursor:"pointer"}}>
            {refreshing ? "…" : "↻ Refresh"}
          </button>
          <Link to="/admin" style={{color:"#B8913A", fontSize:13, alignSelf:"center"}}>← Admin</Link>
        </div>
      </div>

      {err && <div style={{background:"#FDEDED", border:"1px solid #E5A0A0", padding:"10px 14px", borderRadius:8, marginBottom:16}}>{err}</div>}

      {/* ALPHA CHECKLIST (top, hero) */}
      <div style={{background:"linear-gradient(135deg, #1B2340 0%, #2A3358 100%)", color:"#FAF8F3", padding:"22px 26px", borderRadius:14, marginBottom:20}} data-testid="cc-checklist">
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8, marginBottom:14}}>
          <div>
            <div style={{fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:"rgba(255,255,255,0.6)"}}>Alpha Readiness</div>
            <div style={{fontSize:38, fontFamily:"Cormorant Garamond, serif", fontWeight:500}} data-testid="cc-checklist-percent">{cl.ready_percent}%</div>
          </div>
          <div style={{fontSize:13, color:"rgba(255,255,255,0.8)"}}>
            <span style={{color:"#83B26E"}}>● {cl.green} green</span>
            &nbsp;&nbsp;<span style={{color:"#F5C34C"}}>● {cl.yellow} yellow</span>
            &nbsp;&nbsp;<span style={{color:"#F09090"}}>● {cl.red} red</span>
          </div>
        </div>
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:8}}>
          {Object.entries(cl.areas || {}).map(([area, info]) => (
            <div key={area} data-testid={`cc-area-${area}`} style={{
              padding:"10px 12px", background:"rgba(255,255,255,0.06)", borderRadius:8,
              borderLeft:`3px solid ${STATUS_COLORS[info.status] || "#999"}`,
            }}>
              <div style={{fontSize:13, fontWeight:500}}>
                <span style={{marginRight:6}}>{AREA_ICONS[area] || "•"}</span>{area}
              </div>
              <div style={{fontSize:11, color:"rgba(255,255,255,0.65)", marginTop:2}}>{info.detail}</div>
            </div>
          ))}
        </div>
      </div>

      {/* SYSTEM HEALTH */}
      <SectionHeader label="System Health" testid="cc-section-system" />
      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))", gap:10, marginBottom:24}}>
        {Object.entries(sys).map(([k, v]) => (
          <div key={k} data-testid={`cc-sys-${k}`} style={{
            padding:"12px 14px", border:"1px solid rgba(184,145,58,0.25)", borderRadius:12,
            background:"#fff",
          }}>
            <div style={{fontSize:10, letterSpacing:"0.14em", textTransform:"uppercase", color:"rgba(27,35,64,0.55)"}}>{k}</div>
            <div style={{display:"flex", alignItems:"center", gap:8, marginTop:4}}>
              <span style={{width:8, height:8, borderRadius:"50%", background:STATUS_COLORS[v.status] || "#999"}} />
              <span style={{fontSize:14, fontWeight:500}}>{v.status}</span>
            </div>
            {v.environment && <div style={{fontSize:11, color:"rgba(27,35,64,0.55)", marginTop:2}}>{v.environment}</div>}
            {v.latency_ms != null && <div style={{fontSize:11, color:"rgba(27,35,64,0.55)", marginTop:2}}>{v.latency_ms}ms</div>}
          </div>
        ))}
      </div>

      {/* MARKETPLACE HEALTH */}
      <SectionHeader label="Marketplace Health" testid="cc-section-mkt" />
      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:10, marginBottom:24}}>
        <Metric label="Members" value={mkt.members?.total} sub={`+${mkt.members?.today} today · ${mkt.members?.active_today} active`} testid="mkt-members" />
        <Metric label="Active venues" value={mkt.venues?.active} sub={`${mkt.venues?.total} total · +${mkt.venues?.today} today`} testid="mkt-venues" />
        <Metric label="Published workspaces" value={mkt.workspaces?.published} sub={`${mkt.workspaces?.total} total`} testid="mkt-workspaces" />
        <Metric label="Bookings" value={mkt.bookings?.total} sub={`+${mkt.bookings?.today} today`} testid="mkt-bookings" />
        <Metric label="Revenue" value={fmtMoney(mkt.revenue?.gross_cents || 0)} sub={`platform fee ${fmtMoney(mkt.revenue?.platform_fee_cents || 0)}`} testid="mkt-revenue" />
        <Metric label="External clicks" value={mkt.external_clicks?.total} sub={`+${mkt.external_clicks?.today} today`} testid="mkt-external" />
        <Metric label="Hours visits" value={mkt.hours_visits?.total} sub={`+${mkt.hours_visits?.today} today`} testid="mkt-hours" />
        <Metric label="Community posts" value={mkt.community_posts?.total} sub={`+${mkt.community_posts?.today} today`} testid="mkt-community" />
        <Metric label="Feedback" value={mkt.feedback?.total} sub={`+${mkt.feedback?.today} today`} testid="mkt-feedback" />
      </div>

      {/* ACTION REQUIRED + LIVE FEED, side by side */}
      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:24}}>
        <div data-testid="cc-action-required">
          <SectionHeader label="Action Required" testid="cc-section-action" />
          <div style={{background:"#fff", border:"1px solid rgba(184,145,58,0.25)", borderRadius:12, padding:14}}>
            {Object.entries(act).map(([k, v]) => (
              <div key={k} style={{
                display:"flex", justifyContent:"space-between", padding:"8px 4px",
                borderBottom:"1px solid rgba(27,35,64,0.06)",
              }} data-testid={`cc-action-${k}`}>
                <span style={{fontSize:13, color:"rgba(27,35,64,0.8)"}}>{k.replace(/_/g," ")}</span>
                <span style={{
                  fontSize:13, fontWeight:600,
                  color: v > 0 ? "#C05353" : "rgba(27,35,64,0.4)",
                  background: v > 0 ? "rgba(192,83,83,0.08)" : "transparent",
                  padding:"1px 10px", borderRadius:999,
                }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        <div data-testid="cc-live-feed">
          <SectionHeader label="Live Feed" testid="cc-section-feed" />
          <div style={{background:"#fff", border:"1px solid rgba(184,145,58,0.25)", borderRadius:12, padding:14, maxHeight:420, overflowY:"auto"}}>
            {feed.length === 0 && <div style={{color:"rgba(27,35,64,0.55)", fontSize:13, padding:"20px 4px"}}>Quiet on the wire.</div>}
            {feed.map((e, i) => (
              <div key={i} data-testid={`cc-feed-${i}`} style={{
                padding:"8px 4px", borderBottom:"1px solid rgba(27,35,64,0.06)", fontSize:12,
              }}>
                <div style={{display:"flex", alignItems:"center", justifyContent:"space-between"}}>
                  <span style={{fontWeight:500, color:"#1B2340"}}>{e.kind || "event"}</span>
                  <span style={{color:"rgba(27,35,64,0.5)"}}>{e.at?.slice(11,19)}</span>
                </div>
                {e.detail && Object.keys(e.detail).length > 0 && (
                  <div style={{color:"rgba(27,35,64,0.65)", marginTop:3, fontSize:11}}>
                    {Object.entries(e.detail).slice(0,3).map(([k,v]) => (
                      <span key={k} style={{marginRight:10}}><em style={{color:"rgba(27,35,64,0.5)"}}>{k}</em> {String(v).slice(0,40)}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CROSS-LINKS */}
      <div style={{display:"flex", gap:10, flexWrap:"wrap", marginTop:12}}>
        <Link to="/admin/alpha-users" data-testid="cc-link-users" style={{padding:"8px 16px", border:"1px solid #B8913A", borderRadius:999, color:"#B8913A", fontSize:12, textDecoration:"none"}}>Alpha Users →</Link>
        <Link to="/admin/inventory-health" data-testid="cc-link-inventory-health" style={{padding:"8px 16px", border:"1px solid #B8913A", borderRadius:999, color:"#B8913A", fontSize:12, textDecoration:"none"}}>Inventory Health →</Link>
        <Link to="/admin/emails" data-testid="cc-link-emails" style={{padding:"8px 16px", border:"1px solid #B8913A", borderRadius:999, color:"#B8913A", fontSize:12, textDecoration:"none"}}>Email QA →</Link>
        <Link to="/admin/legal" data-testid="cc-link-legal" style={{padding:"8px 16px", border:"1px solid #B8913A", borderRadius:999, color:"#B8913A", fontSize:12, textDecoration:"none"}}>Legal →</Link>
        <Link to="/admin/go-no-go" data-testid="cc-link-gonogo" style={{padding:"8px 16px", background:"#1B2340", color:"#FAF8F3", borderRadius:999, fontSize:12, textDecoration:"none"}}>Go / No-Go Report →</Link>
      </div>
    </div>
  );
}

function SectionHeader({label, testid}) {
  return <h2 data-testid={testid} style={{fontFamily:"Cormorant Garamond, serif", fontSize:20, fontWeight:500, margin:"12px 0 10px", color:"#1B2340"}}>{label}</h2>;
}

function Metric({label, value, sub, testid}) {
  return (
    <div data-testid={testid} style={{
      padding:"14px 16px", border:"1px solid rgba(184,145,58,0.25)", borderRadius:12, background:"#fff",
    }}>
      <div style={{fontSize:10, letterSpacing:"0.14em", textTransform:"uppercase", color:"rgba(27,35,64,0.55)"}}>{label}</div>
      <div style={{fontSize:24, fontFamily:"Cormorant Garamond, serif", marginTop:4}}>{value ?? "—"}</div>
      {sub && <div style={{fontSize:11, color:"rgba(27,35,64,0.55)", marginTop:2}}>{sub}</div>}
    </div>
  );
}


// Session 45.1 — One-shot Rec-Mode production setup button.
function LaunchPartnerButton() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  const run = async (dryRun) => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await api.post("/admin/alpha/launch-partner/apply-recmode", {
        dry_run: dryRun, retire_legacy_studio: true,
      });
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Apply failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setShowConfirm(true)}
        data-testid="cc-launch-partner-apply"
        style={{padding:"7px 14px", background:"#B8913A", color:"#FAF8F3", border:"none", borderRadius:999, fontSize:12, cursor:"pointer"}}>
        Apply Rec-Mode Setup
      </button>

      {showConfirm && (
        <div onClick={() => !busy && setShowConfirm(false)}
          data-testid="cc-launch-partner-modal"
          style={{position:"fixed", inset:0, background:"rgba(27,35,64,0.55)", zIndex:2100, display:"flex", alignItems:"center", justifyContent:"center", padding:24}}>
          <div onClick={(e) => e.stopPropagation()}
            style={{background:"#FAF8F3", borderRadius:14, width:"min(640px,100%)", maxHeight:"90vh", overflow:"auto", padding:"26px 28px", border:"1px solid rgba(184,145,58,0.35)", boxShadow:"0 30px 80px rgba(0,0,0,0.28)", color:"#1B2340"}}>
            <div style={{fontSize:11, letterSpacing:"0.14em", textTransform:"uppercase", color:"#B8913A", fontWeight:600}}>Alpha Ops</div>
            <h2 style={{fontFamily:"Cormorant Garamond, serif", fontSize:26, margin:"2px 0 6px", fontWeight:500}}>Apply Rec-Mode Launch Partner Setup</h2>
            <div style={{fontSize:13, color:"rgba(27,35,64,0.75)", lineHeight:1.6}}>
              This one-shot admin action will, in the current environment:
              <ol style={{paddingLeft:20, margin:"10px 0"}}>
                <li>Retire the legacy <code>/spaces/recmode</code> "Rec-Mode Studio" listing and wipe its <code>recmode_bookings</code> history.</li>
                <li>Attach Stripe Connected Account <code>acct_1T83uXPkhn94o41O</code> to Rec-Mode Silver Spring.</li>
                <li>Verify via the live Stripe API and set v2 pricing: <b>$100/hr, $500/day</b>.</li>
                <li>Cascade Stripe fields to child resources and mark as launch partner.</li>
              </ol>
              Idempotent — safe to re-run.
            </div>

            {err && <div data-testid="cc-launch-partner-error"
              style={{background:"#FDEDED", border:"1px solid #E5A0A0", padding:"10px 14px", borderRadius:8, marginTop:14, fontSize:13}}>{err}</div>}

            {result && (
              <div data-testid="cc-launch-partner-result" style={{marginTop:14, fontSize:13, lineHeight:1.65, background: result.verdict === "ready" ? "#EFF6EB" : "#FDF5E5", border: `1px solid ${result.verdict === "ready" ? "#C7E0B5" : "#E4C879"}`, borderRadius:10, padding:"12px 14px"}}>
                <div style={{fontFamily:"Cormorant Garamond, serif", fontSize:20, marginBottom:6}}>
                  {result.verdict === "ready" ? "✓ Ready" : result.verdict === "needs_attention" ? "⚠ Needs attention" : "· Applied (unverified)"}
                </div>
                <div>{result.verdict_message}</div>
                <ul style={{paddingLeft:20, margin:"10px 0 0"}}>
                  {result.steps.map((s, i) => (
                    <li key={i}><b>{s.name}</b> · <span style={{color:"#B8913A"}}>{s.status}</span>{s.error ? ` — ${s.error}` : ""}</li>
                  ))}
                </ul>
                {result.verification?.verified && (
                  <div style={{marginTop:8, fontSize:12, color:"rgba(27,35,64,0.7)"}}>
                    Stripe · {result.verification.business_name} · {result.verification.environment} · charges {String(result.verification.charges_enabled)} · payouts {String(result.verification.payouts_enabled)}
                  </div>
                )}
              </div>
            )}

            <div style={{display:"flex", gap:10, justifyContent:"flex-end", marginTop:18}}>
              <button type="button" onClick={() => setShowConfirm(false)} disabled={busy}
                style={{padding:"9px 16px", background:"transparent", color:"#1B2340", border:"1px solid rgba(27,35,64,0.25)", borderRadius:999, cursor:"pointer", fontSize:13}}>
                Close
              </button>
              <button type="button" onClick={() => run(true)} disabled={busy}
                data-testid="cc-launch-partner-dry"
                style={{padding:"9px 16px", background:"#fff", color:"#1B2340", border:"1px solid rgba(27,35,64,0.35)", borderRadius:999, cursor:"pointer", fontSize:13}}>
                {busy ? "…" : "Dry run"}
              </button>
              <button type="button" onClick={() => run(false)} disabled={busy}
                data-testid="cc-launch-partner-run"
                style={{padding:"9px 20px", background:"#B8913A", color:"#FAF8F3", border:"none", borderRadius:999, cursor:"pointer", fontSize:13, fontWeight:500}}>
                {busy ? "Applying…" : "Apply now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Session 48.1 · Bulk-apply audit fix — pulls suggested ZIPs from addresses.
function FixZipsButton() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [preview, setPreview] = useState(null);

  const openModal = async () => {
    setShowConfirm(true); setErr(""); setResult(null); setPreview(null); setBusy(true);
    try {
      const r = await api.post("/admin/audit/locations/fix-zips?dry_run=true");
      setPreview(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Preview failed.");
    } finally { setBusy(false); }
  };

  const run = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await api.post("/admin/audit/locations/fix-zips?dry_run=false");
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Fix failed.");
    } finally { setBusy(false); }
  };

  const eligible = preview?.counts?.eligible ?? 0;

  return (
    <>
      <button type="button" onClick={openModal}
        data-testid="cc-fix-zips-open"
        style={{padding:"7px 14px", background:"transparent", color:"#1B2340", border:"1px solid rgba(27,35,64,0.35)", borderRadius:999, fontSize:12, cursor:"pointer"}}>
        Fix Missing ZIPs
      </button>

      {showConfirm && (
        <div onClick={() => !busy && setShowConfirm(false)}
          data-testid="cc-fix-zips-modal"
          style={{position:"fixed", inset:0, background:"rgba(27,35,64,0.55)", zIndex:2100, display:"flex", alignItems:"center", justifyContent:"center", padding:24}}>
          <div onClick={(e) => e.stopPropagation()}
            style={{background:"#FAF8F3", borderRadius:14, width:"min(700px,100%)", maxHeight:"90vh", overflow:"auto", padding:"26px 28px", border:"1px solid rgba(184,145,58,0.35)", boxShadow:"0 30px 80px rgba(0,0,0,0.28)", color:"#1B2340"}}>
            <div style={{fontSize:11, letterSpacing:"0.14em", textTransform:"uppercase", color:"#B8913A", fontWeight:600}}>Admin · Audit</div>
            <h2 style={{fontFamily:"Cormorant Garamond, serif", fontSize:26, margin:"2px 0 6px", fontWeight:500}}>Fix Missing ZIPs</h2>
            <div style={{fontSize:13, color:"rgba(27,35,64,0.75)", lineHeight:1.6, marginBottom:12}}>
              Scans every venue + workspace, extracts the 5-digit ZIP from the <code>address</code> field, and writes it back — only where <code>zip</code> is currently empty. Idempotent.
            </div>

            {busy && !result && <div style={{fontSize:12, color:"rgba(27,35,64,0.6)"}}>Scanning…</div>}

            {preview && !result && (
              <div style={{background:"#FDF5E5", border:"1px solid #E4C879", borderRadius:10, padding:"12px 14px", marginBottom:12}}>
                <div style={{fontFamily:"Cormorant Garamond, serif", fontSize:20, marginBottom:4}}>
                  Preview — {eligible} record{eligible === 1 ? "" : "s"} will be patched
                </div>
                <div style={{fontSize:12, color:"rgba(27,35,64,0.75)"}}>
                  Scanned {preview.counts.scanned} · Eligible {preview.counts.eligible}
                </div>
              </div>
            )}

            {result && (
              <div data-testid="cc-fix-zips-result"
                style={{background:"#EFF6EB", border:"1px solid #C7E0B5", borderRadius:10, padding:"12px 14px", marginBottom:12}}>
                <div style={{fontFamily:"Cormorant Garamond, serif", fontSize:20, marginBottom:4}}>
                  ✓ {result.counts.patched} record{result.counts.patched === 1 ? "" : "s"} patched
                </div>
                <div style={{fontSize:12, color:"rgba(27,35,64,0.75)"}}>
                  Scanned {result.counts.scanned} · Eligible {result.counts.eligible} · Skipped {result.counts.skipped}
                </div>
              </div>
            )}

            {(preview?.results?.length || result?.results?.length) ? (
              <div style={{maxHeight:280, overflow:"auto", border:"1px solid rgba(27,35,64,0.1)", borderRadius:10, background:"#fff"}}>
                <table style={{width:"100%", borderCollapse:"collapse", fontSize:12}}>
                  <thead>
                    <tr style={{background:"rgba(184,145,58,0.08)", textAlign:"left"}}>
                      <th style={{padding:"7px 10px"}}>Name</th>
                      <th style={{padding:"7px 10px"}}>Type</th>
                      <th style={{padding:"7px 10px"}}>ZIP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(result?.results || preview?.results || []).map((r, i) => (
                      <tr key={i} style={{borderTop:"1px solid rgba(27,35,64,0.05)"}}>
                        <td style={{padding:"6px 10px"}}>{r.name}</td>
                        <td style={{padding:"6px 10px", color:"rgba(27,35,64,0.6)"}}>{r.kind}</td>
                        <td style={{padding:"6px 10px", fontFamily:"ui-monospace, monospace", color:"#B8913A"}}>{r.zip}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            {err && <div style={{background:"#FDEDED", border:"1px solid #E5A0A0", padding:"10px 14px", borderRadius:8, marginTop:12, fontSize:13}}>{err}</div>}

            <div style={{display:"flex", gap:10, justifyContent:"flex-end", marginTop:16}}>
              <button type="button" onClick={() => setShowConfirm(false)} disabled={busy}
                style={{padding:"9px 16px", background:"transparent", color:"#1B2340", border:"1px solid rgba(27,35,64,0.25)", borderRadius:999, cursor:"pointer", fontSize:13}}>
                {result ? "Done" : "Cancel"}
              </button>
              {!result && (
                <button type="button" onClick={run} disabled={busy || eligible === 0}
                  data-testid="cc-fix-zips-run"
                  style={{padding:"9px 20px", background: (busy || eligible === 0) ? "rgba(184,145,58,0.5)" : "#B8913A", color:"#FAF8F3", border:"none", borderRadius:999, cursor: (busy || eligible === 0) ? "not-allowed" : "pointer", fontSize:13, fontWeight:500}}>
                  {busy ? "Applying…" : `Fix ${eligible || ""}`.trim()}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Session 55 · Bulk audit-fix modal shared by State + Neighborhood buttons.
function AuditFixButton({ endpoint, label, title, description, testidPrefix, resultColumn, actionVerb }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [preview, setPreview] = useState(null);

  const openModal = async () => {
    setShowConfirm(true); setErr(""); setResult(null); setPreview(null); setBusy(true);
    try {
      const r = await api.post(`${endpoint}?dry_run=true`);
      setPreview(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Preview failed.");
    } finally { setBusy(false); }
  };

  const run = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await api.post(`${endpoint}?dry_run=false`);
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Fix failed.");
    } finally { setBusy(false); }
  };

  const eligible = preview?.counts?.eligible ?? 0;
  const patchedCount = result?.counts?.patched ?? 0;
  const rows = result?.results || preview?.results || [];
  const doneVerb = actionVerb === "Clear" ? "cleared" : "patched";

  return (
    <>
      <button type="button" onClick={openModal}
        data-testid={`${testidPrefix}-open`}
        style={{padding:"7px 14px", background:"transparent", color:"#1B2340", border:"1px solid rgba(27,35,64,0.35)", borderRadius:999, fontSize:12, cursor:"pointer"}}>
        {label}
      </button>

      {showConfirm && (
        <div onClick={() => !busy && setShowConfirm(false)}
          data-testid={`${testidPrefix}-modal`}
          style={{position:"fixed", inset:0, background:"rgba(27,35,64,0.55)", zIndex:2100, display:"flex", alignItems:"center", justifyContent:"center", padding:24}}>
          <div onClick={(e) => e.stopPropagation()}
            style={{background:"#FAF8F3", borderRadius:14, width:"min(700px,100%)", maxHeight:"90vh", overflow:"auto", padding:"26px 28px", border:"1px solid rgba(184,145,58,0.35)", boxShadow:"0 30px 80px rgba(0,0,0,0.28)", color:"#1B2340"}}>
            <div style={{fontSize:11, letterSpacing:"0.14em", textTransform:"uppercase", color:"#B8913A", fontWeight:600}}>Admin · Audit</div>
            <h2 style={{fontFamily:"Cormorant Garamond, serif", fontSize:26, margin:"2px 0 6px", fontWeight:500}}>{title}</h2>
            <div style={{fontSize:13, color:"rgba(27,35,64,0.75)", lineHeight:1.6, marginBottom:12}}>
              {description}
            </div>

            {busy && !result && <div style={{fontSize:12, color:"rgba(27,35,64,0.6)"}}>Scanning…</div>}

            {preview && !result && (
              <div style={{background:"#FDF5E5", border:"1px solid #E4C879", borderRadius:10, padding:"12px 14px", marginBottom:12}}>
                <div style={{fontFamily:"Cormorant Garamond, serif", fontSize:20, marginBottom:4}}>
                  Preview — {eligible} record{eligible === 1 ? "" : "s"} will be {doneVerb}
                </div>
                <div style={{fontSize:12, color:"rgba(27,35,64,0.75)"}}>
                  Scanned {preview.counts.scanned} · Eligible {preview.counts.eligible}
                </div>
              </div>
            )}

            {result && (
              <div data-testid={`${testidPrefix}-result`}
                style={{background:"#EFF6EB", border:"1px solid #C7E0B5", borderRadius:10, padding:"12px 14px", marginBottom:12}}>
                <div style={{fontFamily:"Cormorant Garamond, serif", fontSize:20, marginBottom:4}}>
                  ✓ {patchedCount} record{patchedCount === 1 ? "" : "s"} {doneVerb}
                </div>
                <div style={{fontSize:12, color:"rgba(27,35,64,0.75)"}}>
                  Scanned {result.counts.scanned} · Eligible {result.counts.eligible} · Skipped {result.counts.skipped}
                </div>
              </div>
            )}

            {rows.length > 0 && (
              <div style={{maxHeight:280, overflow:"auto", border:"1px solid rgba(27,35,64,0.1)", borderRadius:10, background:"#fff"}}>
                <table style={{width:"100%", borderCollapse:"collapse", fontSize:12}}>
                  <thead>
                    <tr style={{background:"rgba(184,145,58,0.08)", textAlign:"left"}}>
                      <th style={{padding:"7px 10px"}}>Name</th>
                      <th style={{padding:"7px 10px"}}>Type</th>
                      <th style={{padding:"7px 10px"}}>{resultColumn.label}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} style={{borderTop:"1px solid rgba(27,35,64,0.05)"}}>
                        <td style={{padding:"6px 10px"}}>{r.name}</td>
                        <td style={{padding:"6px 10px", color:"rgba(27,35,64,0.6)"}}>{r.kind}</td>
                        <td style={{padding:"6px 10px", fontFamily:"ui-monospace, monospace", color:"#B8913A"}}>{r[resultColumn.key]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {err && <div style={{background:"#FDEDED", border:"1px solid #E5A0A0", padding:"10px 14px", borderRadius:8, marginTop:12, fontSize:13}}>{err}</div>}

            <div style={{display:"flex", gap:10, justifyContent:"flex-end", marginTop:16}}>
              <button type="button" onClick={() => setShowConfirm(false)} disabled={busy}
                style={{padding:"9px 16px", background:"transparent", color:"#1B2340", border:"1px solid rgba(27,35,64,0.25)", borderRadius:999, cursor:"pointer", fontSize:13}}>
                {result ? "Done" : "Cancel"}
              </button>
              {!result && (
                <button type="button" onClick={run} disabled={busy || eligible === 0}
                  data-testid={`${testidPrefix}-run`}
                  style={{padding:"9px 20px", background: (busy || eligible === 0) ? "rgba(184,145,58,0.5)" : "#B8913A", color:"#FAF8F3", border:"none", borderRadius:999, cursor: (busy || eligible === 0) ? "not-allowed" : "pointer", fontSize:13, fontWeight:500}}>
                  {busy ? "Applying…" : `${actionVerb} ${eligible || ""}`.trim()}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function FixStatesButton() {
  return (
    <AuditFixButton
      endpoint="/admin/audit/locations/fix-states"
      label="Fix Missing States"
      title="Fix Missing States"
      description={<>Scans every venue + workspace, extracts the 2-letter US state (e.g. <code>MD</code>, <code>NH</code>) from the <code>address</code> field, and writes it back — only where <code>state</code> is currently empty. Idempotent.</>}
      testidPrefix="cc-fix-states"
      resultColumn={{ key: "state", label: "State" }}
      actionVerb="Fix"
    />
  );
}

function FixNeighborhoodsButton() {
  return (
    <AuditFixButton
      endpoint="/admin/audit/locations/fix-neighborhoods"
      label="Clear Bogus Neighborhoods"
      title="Clear Bogus Neighborhoods"
      description={<>Clears the <code>neighborhood</code> field on venues + workspaces whose value is identical to the record's <code>city</code> or <code>state</code>. Leaves genuine neighborhoods intact. Idempotent.</>}
      testidPrefix="cc-fix-neighborhoods"
      resultColumn={{ key: "prev_neighborhood", label: "Was" }}
      actionVerb="Clear"
    />
  );
}

// Session 55.z · Repair listings where admin photos exist but the card `img`
// + detail `gallery/images` never cascaded (pre-fix uploads on prod).
function BackfillCardPhotosButton() {
  return (
    <AuditFixButton
      endpoint="/admin/audit/locations/backfill-card-photos"
      label="Backfill Card Photos"
      title="Backfill Card Photos"
      description={<>Repairs every workspace + venue where you uploaded photos in Admin but the marketplace card + detail hero are still blank. Mirrors <code>photos[0] → img</code> and <code>photos → gallery/images</code>. Run this on <b>production</b> after redeploying — one click fixes all pre-cascade uploads. Idempotent.</>}
      testidPrefix="cc-backfill-photos"
      resultColumn={{ key: "photo_count", label: "Photos" }}
      actionVerb="Backfill"
    />
  );
}

// Session 55.ab · Force every workspace to re-inherit location metadata from
// its parent venue so the state/city/ZIP filters and radius search stay in
// sync as venues get edited.
function ResyncWorkspaceLocationsButton() {
  return (
    <AuditFixButton
      endpoint="/admin/audit/locations/resync-workspace-locations"
      label="Resync Workspace Locations"
      title="Resync Workspace Locations from Venues"
      description={<>Forces every workspace to inherit fresh <code>city / state / ZIP / lat / lng / country / neighborhood</code> from its parent venue. Fixes any drift when the state dropdown or ZIP search returns fewer results than expected. Same denormalizer that runs on every venue Save, but batched across the whole inventory. Idempotent.</>}
      testidPrefix="cc-resync-ws-locs"
      resultColumn={{ key: "children_matched", label: "Children" }}
      actionVerb="Resync"
    />
  );
}

// Session 55.af · Batch-geocode every active venue missing lat/lng. Fixes
// the "N venues missing geocoding" Go/No-Go warning. Rate-limited to 25 per
// run (Nominatim's 1 req/sec ceiling → ~25 sec per click).
function GeocodeMissingButton() {
  return (
    <AuditFixButton
      endpoint="/admin/audit/locations/geocode-missing"
      label="Geocode Missing Venues"
      title="Geocode Missing Venues"
      description={<>Iterates every active venue with a blank <code>latitude / longitude</code> and calls the Nominatim (OpenStreetMap) geocoder using each venue's address text. Persists coordinates + cascades <code>venue_latitude / venue_longitude</code> to every child workspace so radius search and map view pick them up immediately. Free, no API key. Rate-limited to 25 records per click. Idempotent.</>}
      testidPrefix="cc-geocode-missing"
      resultColumn={{ key: "addr", label: "Address" }}
      actionVerb="Geocode"
    />
  );
}


